import { SUPPORT_EMAIL } from './contact';

/**
 * The one place a client email becomes HTML.
 *
 * Every mail to a client is written as plain text in the templates screen —
 * that stays the source an admin edits — and passes through here exactly
 * once, in `ClientMailService.send`. The admin screen's Preview tab calls the
 * same `renderMailHtml` before showing anything, so there is one path from
 * an edit to a rendered email, not a preview path and a send path that can
 * drift apart:
 *
 *   edit -> renderMailHtml -> Preview (Visual)
 *   edit -> toPlainText     -> Preview (Plain text)
 *   edit -> renderMailHtml -> SMTP html part
 *   edit -> toPlainText     -> SMTP text part
 *
 * Below that single pair of functions, three layers, each with exactly one
 * copy:
 *  - TOKENS: every colour, spacing and font value a template could reach for.
 *  - EmailShell: the document, preheader, outer background, container,
 *    header and footer — identical on every template.
 *  - blocks (heading, paragraph, quiet, button, signOff): the repeating
 *    pieces a body is made of. A template never writes its own <table> or
 *    <td>; it hands EmailShell text, and text becomes blocks.
 *
 * A blank line still splits a body into blocks. A marker at the start of a
 * block says what kind it is — the same markers a template's plain-text part
 * shows verbatim once `toPlainText` strips them off:
 *  - `# ` — the headline, the one sentence the email is about. Large, and
 *    whatever is `**bold**` inside it is set in the brand colour: that is
 *    how a count reads as the news rather than as a word in a paragraph.
 *  - `! ` — a callout panel, optionally opening with `{Kept private}`: that
 *    becomes its eyebrow, the line that says what kind of note it is. Its
 *    first line carries, the rest is muted.
 *  - `= ` — a value to read off and type, on its own panel: a code.
 *  - `-- ` — a longer value to copy as-is (the bio notice), same panel shape
 *    as a code but smaller type. Two dashes, not one: `=` is already the
 *    code, and a single `-` is ordinary prose.
 *  - `> ` — fine print, said quieter than the rest (a caption under the
 *    button, a settings aside).
 *  - `**bold**` — a word worth landing on inline, most often a number.
 *  - ` | ` between phrases — a divider, drawn in grey so the numbers either
 *    side of it carry the line: `**4** searches | **3** new pages`. Only a
 *    pipe with a space on both sides, which is not something prose contains
 *    by accident.
 *  - `[Label](https://…)` alone on a block — the button, with that label.
 *    A bare link alone is the same button under a generic one, because a
 *    button that says what happens next beats a button that says "Open".
 *
 * Table layout and inline styles throughout, not a <style> block, flexbox or
 * CSS grid: the mail clients that matter (Gmail, Outlook, Apple Mail) strip
 * or ignore enough of a <style> block, and enough of modern layout CSS, that
 * anything not a table with inlined styles is a coin flip. Still worth an
 * actual test send before anything ships to clients — a browser's own
 * rendering of this HTML is not proof of what Gmail or Outlook will show.
 */

const TOKENS = {
  width: 560,
  font: '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif',
  color: {
    primary: '#6d4aff',
    primaryDark: '#4737c2',
    ink: '#171a2b',
    muted: '#6b7089',
    line: '#e6e8f2',
    /* Two tints, not one. `background` is the gutter the card lies on and
       `band` is the card's own header, footer and callout — they used to be
       the same #f2f3ff, so the gutter read as a second card around the
       first: a lavender box, a white box inside it, lavender bands inside
       that. Near-white here means the email is one card with a tinted top
       and bottom, which is what it is. */
    background: '#f7f8fc',
    band: '#f2f3ff',
    /* Soft pink: the client rail's `--rail-acc` at 11% on white
       (`color-mix(in srgb, #ff4d8d 11%, transparent)`). Mail clients do not
       honour CSS variables or `color-mix`, so the mix is baked in. */
    noticeBand: '#ffebf2',
    /* Stronger than `line`, which is a hairline meant to be seen only as
       structure: a divider inside a sentence has to read as punctuation a
       reader's eye can skip, not as a border. */
    divider: '#c9ccdd',
    surface: '#ffffff',
  },
  spacing: {
    page: '24px 12px',
    header: '20px 32px',
    body: '28px 32px 20px',
    footer: '20px 32px 26px',
  },
  radius: {
    container: '14px',
    button: '8px',
  },
};

/**
 * Quotes too, not only the angle brackets.
 *
 * `linkify` below drops whatever it matched into `href="…"`, and the URL
 * pattern happily matches a double quote — so a body containing
 * `https://x/"` closed the attribute early and everything after it in that
 * URL became markup of the reader's choosing, in mail sent to a client. The
 * bodies are admin-written rather than public, which is what made it a hole
 * nobody would trip over by accident and nothing would catch.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const URL_RE = /https?:\/\/[^\s<]+[^\s<.,;:'")\]]/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
/** `[Label](https://…)`: a link that reads as words rather than as an
 * address. Alone on a block it is the button; inside a sentence it is an
 * ordinary link. */
const MD_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

function linkify(escaped: string): string {
  return escaped.replace(
    URL_RE,
    (url) => `<a href="${url}" style="color:${TOKENS.color.primary};text-decoration:underline;">${url}</a>`,
  );
}

/** ` | ` becomes a grey divider. A pipe the same colour as the text reads
 * as a character somebody typed; grey, it reads as the rule between two
 * figures, which is its whole job in a stat line. */
function dividers(escaped: string): string {
  return escaped.replace(
    / \| /g,
    // The span swallows the spaces either side of the pipe, so its own
    // padding is what keeps the figures apart — 8px, rather than the
    // 4px-ish a plain word space would have left.
    `<span style="color:${TOKENS.color.divider};padding:0 8px;">|</span>`,
  );
}

function boldify(escaped: string): string {
  return escaped.replace(BOLD_RE, '<strong>$1</strong>');
}

/** The same bold, in the brand colour — for the headline only, where the
 * emphasised words are the news itself. */
function boldifyBright(escaped: string): string {
  return escaped.replace(BOLD_RE, `<strong style="color:${TOKENS.color.primary};">$1</strong>`);
}

function mdLinks(escaped: string): string {
  return escaped.replace(
    MD_LINK_RE,
    (_, label: string, href: string) =>
      `<a href="${escapeHtml(href)}" style="color:${TOKENS.color.primary};text-decoration:underline;">${label}</a>`,
  );
}

function stripBold(raw: string): string {
  return raw.replace(BOLD_RE, '$1');
}

/** What a plain-text reader gets for `[Label](url)`: both halves, because
 * the words alone would leave them with nowhere to go and the address alone
 * would lose what it is. */
function stripMarkers(raw: string): string {
  return stripBold(raw).replace(MD_LINK_RE, '$1: $2');
}

/** Escaped, then the markers, in an order where none of them can match
 * something an earlier one produced: `[Label](url)` before bare URLs, or
 * `linkify` would eat the address out of the middle of the markdown. */
function inlineHtml(raw: string, bold: (value: string) => string = boldify): string {
  return dividers(linkify(mdLinks(bold(escapeHtml(raw))))).replace(/\n/g, '<br>');
}

/* ── blocks: the repeating pieces a body is made of ─────────────────────── */

const blocks = {
  /** The headline, and the only large type in the email: one sentence,
   * with the words that are the news set in the brand colour. */
  heading(html: string): string {
    return `<h1 style="margin:0 0 14px;font-size:26px;font-weight:700;line-height:1.25;letter-spacing:-0.02em;color:${TOKENS.color.ink};">${html}</h1>`;
  },

  /** A panel for the one thing worth lifting out of the prose — what we did
   * with her material, what happens next.
   *
   * An eyebrow, a line that carries, and a line that explains. The eyebrow
   * is the part an icon was standing in for, and it does the job an icon
   * cannot: `KEPT PRIVATE` and `WHAT HAPPENS NEXT` are different notes, and
   * a shield beside either says only that somebody wanted a shield there.
   * It is also the one version of this that no mail client can take away —
   * Gmail strips inline SVG, blocks `data:` images and holds an external one
   * behind "display images", so an icon here is a grey placeholder in the
   * client that matters most. */
  callout(eyebrow: string, lines: string[]): string {
    const [first, ...rest] = lines;
    return [
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:4px 0 20px;background:${TOKENS.color.band};border-radius:10px;">`,
      `<tr>`,
      `<td width="3" style="background:${TOKENS.color.primary};border-radius:10px 0 0 10px;font-size:0;line-height:0;">&nbsp;</td>`,
      `<td style="padding:14px 16px;">`,
      ...(eyebrow
        ? [
            `<p style="margin:0 0 6px;font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:${TOKENS.color.primary};">${eyebrow}</p>`,
          ]
        : []),
      `<p style="margin:0;font-size:14px;font-weight:700;line-height:1.5;color:${TOKENS.color.ink};">${first ?? ''}</p>`,
      ...rest.map(
        (line) =>
          `<p style="margin:4px 0 0;font-size:13px;line-height:1.55;color:${TOKENS.color.muted};">${line}</p>`,
      ),
      `</td></tr></table>`,
    ].join('');
  },

  /** A one-off value a person has to read off the screen and type: a
   * confirmation code, and nothing else so far. Large, spaced and on its own
   * panel, because the alternative is six digits buried mid-sentence, which
   * is where every mistyped code comes from. Letter-spacing on a centred
   * cell rather than a `<pre>`: Outlook ignores the tag and keeps the
   * spacing. */
  code(html: string): string {
    return [
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:4px 0 20px;background:${TOKENS.color.band};border-radius:10px;">`,
      `<tr><td align="center" style="padding:18px 16px;font-family:${TOKENS.font};font-size:30px;font-weight:700;` +
        `letter-spacing:0.22em;line-height:1.1;color:${TOKENS.color.ink};">${html}</td></tr>`,
      `</table>`,
    ].join('');
  },

  /** Text to copy into a profile header: same panel as a code, smaller type,
   * left-aligned so a multi-line notice stays readable. Pink band rather than
   * the lavender one so it is not mistaken for a confirmation code. */
  notice(html: string): string {
    return [
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:4px 0 20px;background:${TOKENS.color.noticeBand};border-radius:10px;">`,
      `<tr><td align="left" style="padding:18px 16px;font-size:14px;font-weight:700;color:${TOKENS.color.ink};">${html}</td></tr>`,
      `</table>`,
    ].join('');
  },

  paragraph(html: string): string {
    return `<p style="margin:0 0 16px;font-size:15px;line-height:1.6;color:${TOKENS.color.ink};">${html}</p>`;
  },

  /** Fine print: a settings aside, a footer line — read after the point
   * above it has already landed, not competing with it. */
  quiet(html: string): string {
    return `<p style="margin:0 0 8px;font-size:13px;line-height:1.6;color:${TOKENS.color.muted};">${html}</p>`;
  },

  /** Full width, and the padding is on the cell rather than on the anchor:
   * Word's engine, which draws Outlook on Windows, ignores padding on an
   * inline-block `<a>`, so the button collapsed to the height of its own
   * text there while looking right everywhere else.
   *
   * The arrow is `&#10132;` (U+2794, heavy wide-headed), not the plain
   * `&#8594;` (U+2192), and that is measured rather than taste. At the same
   * 19px and weight 700, rendered in the engine the preview uses:
   *
   *   U+2192, any of Arial / Helvetica / Liberation Sans …  3px of stroke
   *   U+2794, same stack …                                 10px of stroke
   *
   * The three font names give the same answer because Arial and Helvetica
   * are not installed on a Linux box at all — fontconfig substitutes
   * Liberation Sans, whose U+2192 is a hairline that `font-weight` does not
   * thicken, since the bold face draws the glyph identically. So no amount
   * of `font-family` or `font-weight` on U+2192 was ever going to make it
   * look like the mock; the glyph itself had to change.
   *
   * U+2794 is a dingbat with no emoji presentation (unlike U+27A1 ➡, which
   * renders as a colour emoji on phones), and it is carried by Segoe UI
   * Symbol on Windows, Apple Symbols on macOS and iOS, Noto Sans Symbols on
   * Android and DejaVu Sans on Linux — all four named in the stack below,
   * ending in `sans-serif` so a client with none of them still resolves
   * something rather than drawing a notdef box.
   *
   * Padding and a baseline nudge rather than a `&nbsp;`: Word's engine
   * (Outlook desktop) honours padding on an inline element, ignores
   * `display:inline-block`, and drops the glyph low without the
   * `vertical-align` — which is what made the first version read as
   * punctuation somebody typed.
   *
   * `href` is escaped here rather than by the caller: this is the one place
   * a whole block of body text is used as a URL instead of as words, so it
   * is the one place that skipped `inlineHtml` and reached the attribute
   * raw. A body line of `https://x/"onmouseover="…` wrote its own attribute
   * into a client's email. */
  button(href: string, label: string): string {
    const safeHref = escapeHtml(href);
    return [
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:4px 0 10px;">`,
      `<tr><td align="center" style="padding:15px 24px;border-radius:${TOKENS.radius.button};background:${TOKENS.color.primary};">`,
      `<a href="${safeHref}" style="display:block;color:#ffffff;font-weight:700;` +
        `font-size:15px;line-height:1;text-decoration:none;border-radius:${TOKENS.radius.button};font-family:${TOKENS.font};">` +
        `${label}<span style="padding-left:11px;font-family:Arial,Helvetica,&quot;Segoe UI Symbol&quot;,` +
        `&quot;Apple Symbols&quot;,&quot;Noto Sans Symbols&quot;,&quot;DejaVu Sans&quot;,sans-serif;font-size:17px;` +
        `font-weight:700;line-height:1;vertical-align:-1px;">&#10132;</span></a>`,
      `</td></tr></table>`,
    ].join('');
  },

  signOff(): string {
    return [
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:8px 0 18px;"><tr><td style="border-top:1px solid ${TOKENS.color.line};font-size:0;line-height:0;">&nbsp;</td></tr></table>`,
      `<p style="margin:16px 0 0;font-size:15px;line-height:1.6;color:${TOKENS.color.ink};">Thanks,</p>`,
      `<p style="margin:0;font-size:15px;line-height:1.6;color:${TOKENS.color.ink};">The Snily team</p>`,
    ].join('\n');
  },
};

type ParsedBlock =
  | { type: 'heading' | 'quiet' | 'paragraph' | 'callout' | 'code' | 'notice'; content: string }
  | { type: 'button'; content: string; label: string };

/** The one place a raw block of text is told apart from a heading, a quiet
 * aside or a button. `renderMailHtml` and `toPlainText` both call this, so a
 * marker recognised by one is recognised by the other. */
function classify(raw: string): ParsedBlock {
  const labelled = raw.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
  if (labelled) return { type: 'button', content: labelled[2], label: labelled[1] };
  if (/^https?:\/\/\S+$/.test(raw)) return { type: 'button', content: raw, label: 'View in Snily' };
  // `/^# ?/` rather than `'# '`: a marker whose token came back empty is
  // left standing alone, and `#` on its own is not a paragraph a client
  // should ever read — `renderBlock` drops it once it has no content.
  if (/^# ?/.test(raw)) return { type: 'heading', content: raw.replace(/^# ?/, '') };
  if (/^! ?/.test(raw)) return { type: 'callout', content: raw.replace(/^! ?/, '') };
  if (/^= ?/.test(raw)) return { type: 'code', content: raw.replace(/^= ?/, '') };
  // Two dashes before `=` would never match; before a single `-` would eat
  // ordinary list-like prose. Require the second dash so `-- notice` is a
  // panel and `- notice` stays a paragraph.
  if (/^-- ?/.test(raw)) return { type: 'notice', content: raw.replace(/^-- ?/, '') };
  if (/^> ?/.test(raw)) return { type: 'quiet', content: raw.replace(/^> ?/, '') };
  return { type: 'paragraph', content: raw };
}

function renderBlock(block: ParsedBlock): string {
  /*
    A token that arrived empty leaves its block with nothing in it, and the
    block still renders: `! {At the limit} {{note}}` with no note drew a
    panel containing an eyebrow and a blank line, and `# {{title}}` with no
    title drew a lone `#`. Both reached clients — every sample value on the
    templates screen is filled in, so the preview could never show it.
  */
  if (block.type !== 'button' && block.content.replace(/\{[^}]*\}/, '').trim() === '') return '';
  switch (block.type) {
    case 'heading':
      return blocks.heading(inlineHtml(block.content, boldifyBright));
    case 'callout': {
      // `{Kept private}` off the front of the first line, if it is there:
      // braces rather than brackets, which `[Label](url)` already owns, and
      // never a `{{token}}`, whose pattern needs two of them.
      const labelled = block.content.match(/^\{([^}]+)\}\s*/);
      const eyebrow = labelled ? escapeHtml(labelled[1]) : '';
      const rest = labelled ? block.content.slice(labelled[0].length) : block.content;
      return blocks.callout(
        eyebrow,
        rest.split('\n').map((line) => inlineHtml(line.trim())),
      );
    }
    case 'code':
      return blocks.code(inlineHtml(block.content));
    case 'notice':
      return blocks.notice(inlineHtml(block.content));
    case 'quiet':
      return blocks.quiet(inlineHtml(block.content));
    case 'button':
      return blocks.button(block.content, escapeHtml(block.label));
    case 'paragraph':
      return blocks.paragraph(inlineHtml(block.content));
  }
}

/* ── EmailShell: the document every template is rendered inside ────────── */

/** Keeps the inbox from pulling the first visible body words into the
 * preheader slot after our own preview text. Hidden; never shown as content. */
function preheaderPad(): string {
  return Array.from({ length: 40 }, () => '&#847;&zwnj;&nbsp;').join('');
}

function emailShell({
  subject,
  preheader,
  bodyHtml,
  settingsUrl,
}: {
  subject: string;
  preheader: string;
  bodyHtml: string;
  settingsUrl?: string;
}): string {
  const t = TOKENS;
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background:${t.color.background};font-family:${t.font};">
<div style="display:none;max-height:0;overflow:hidden;opacity:0;mso-hide:all;">${preheader}${preheaderPad()}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${t.color.background};padding:${t.spacing.page};">
<tr><td align="center">
<table role="presentation" width="${t.width}" cellpadding="0" cellspacing="0" style="max-width:${t.width}px;width:100%;background:${t.color.surface};border-radius:${t.radius.container};overflow:hidden;border:1px solid ${t.color.line};">
<tr><td style="padding:${t.spacing.header};background:${t.color.band};border-bottom:1px solid ${t.color.line};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
<td align="left" style="font-size:20px;font-weight:700;color:${t.color.primary};letter-spacing:-0.01em;">Snily</td>
<td align="right" style="font-size:11.5px;color:${t.color.muted};">Content removal for creators</td>
</tr></table>
</td></tr>
<tr><td style="padding:${t.spacing.body};">
${bodyHtml}
${blocks.signOff()}
</td></tr>
<tr><td style="padding:${t.spacing.footer};background:${t.color.band};border-top:1px solid ${t.color.line};">
<p style="margin:0;font-size:13px;font-weight:700;line-height:1.5;color:${t.color.ink};">Snily</p>
<p style="margin:0 0 14px;font-size:12px;line-height:1.5;color:${t.color.muted};">Content removal for creators</p>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 12px;"><tr><td style="border-top:1px solid ${t.color.line};font-size:0;line-height:0;">&nbsp;</td></tr></table>
<p style="margin:0;font-size:12px;line-height:1.6;color:${t.color.muted};">Questions? <a href="mailto:${SUPPORT_EMAIL}" style="color:${t.color.primary};">${SUPPORT_EMAIL}</a></p>
<p style="margin:0;font-size:12px;line-height:1.6;color:${t.color.muted};">You received this email because you have a Snily account.${
    settingsUrl
      ? ` <a href="${escapeHtml(settingsUrl)}" style="color:${t.color.primary};">Choose which emails you get</a>.`
      : ''
  }</p>
</td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

export interface MailHtmlInput {
  /** Already-rendered — every `{{token}}` filled in, same string that goes
   * into the plain-text part before `toPlainText` strips its markers off. */
  subject: string;
  text: string;
  /**
   * Inbox line under the subject. When set, this is what the hidden
   * preheader carries; when absent or blank, the first body block is used
   * (the behaviour before templates had their own preview field).
   */
  preview?: string;
  /** Where a reader turns this kind of mail off, if the caller knows it.
   * The footer said "you received this because you have an account" and
   * offered nothing to do about it, which is the line every unwanted email
   * ends with. `ClientMailService` passes the app's own settings screen. */
  settingsUrl?: string;
}

function splitBlocks(text: string): string[] {
  return text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
}

/** The one canonical HTML renderer — see the file doc comment for the block
 * markers it reads. */
export function renderMailHtml({ subject, text, preview, settingsUrl }: MailHtmlInput): string {
  const raw = splitBlocks(text);
  const parsed = raw.map(classify);
  const bodyHtml = parsed.map(renderBlock).filter(Boolean).join('\n');
  const fromBody = stripMarkers(parsed[0]?.content ?? '');
  const chosen = (preview?.trim() ? preview.trim() : fromBody).slice(0, 140);
  const preheader = escapeHtml(chosen);
  return emailShell({ subject, preheader, bodyHtml, settingsUrl });
}

/** The plain-text part of the same message: the `#`/`>`/`**` markers
 * `renderMailHtml` turns into a heading, fine print or bold are meant for
 * the rendered email only, so a plain-text inbox — or this screen's own
 * Plain text preview — never shows the raw punctuation. */
export function toPlainText(text: string): string {
  return splitBlocks(text)
    .map((raw) => {
      const block = classify(raw);
      // A button keeps its label beside the address: `View new findings`
      // alone tells a plain-text reader nothing to click.
      if (block.type === 'button') {
        return block.label === 'View in Snily' ? block.content : `${block.label}: ${block.content}`;
      }
      // A callout's eyebrow keeps its words in plain text too, as the label
      // it is: `Kept private: we keep the details…` reads, `{Kept private}`
      // does not.
      if (block.type === 'callout') {
        const labelled = block.content.match(/^\{([^}]+)\}\s*/);
        if (labelled) {
          return `${labelled[1]}: ${stripMarkers(block.content.slice(labelled[0].length))}`;
        }
      }
      return stripMarkers(block.content);
    })
    .join('\n\n');
}
